import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Checkbox } from '@/components/ui/checkbox';
import { Eye, EyeOff, UserPlus } from 'lucide-react';

export default function SignupPage() {
  const location = useLocation();
  const [email, setEmail] = useState(location.state?.email || '');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [consentPromo, setConsentPromo] = useState(false);
  const [is18Plus, setIs18Plus] = useState(false);
  const [loading, setLoading] = useState(false);
  const { setUser } = useAuth();
  const navigate = useNavigate();

  const handleSignup = async (e) => {
    e.preventDefault();
    if (!is18Plus) { toast.error('You must confirm you are 18+ to register'); return; }
    if (!consentPromo) { toast.error('You must agree to receive promotional emails'); return; }
    if (password.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    setLoading(true);
    try {
      const res = await api.post('/auth/register', { email, password, consent_promotional: consentPromo, is_18_plus: is18Plus });
      localStorage.setItem('casinoiq_token', res.data.token);
      setUser(res.data.user);
      toast.success('Account created successfully!');
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignup = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-[#1a1f2e] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <Link to="/" className="text-2xl font-bold text-white tracking-tight inline-block">Casino<span className="text-[#4d7cfe]">IQ</span></Link>
          <p className="text-[#a0a8b8] text-sm mt-2">Create your free account</p>
        </div>

        <div className="ciq-card">
          <form onSubmit={handleSignup} className="space-y-4">
            <div>
              <label className="text-sm font-medium text-[#a0a8b8] mb-1.5 block">Email</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                className="w-full h-11 px-4 rounded-lg bg-[#1a1f2e] border border-white/10 text-white text-sm placeholder:text-[#64748b] ciq-input focus:outline-none"
                placeholder="you@example.com" data-testid="signup-email-input" />
            </div>
            <div>
              <label className="text-sm font-medium text-[#a0a8b8] mb-1.5 block">Password</label>
              <div className="relative">
                <input type={showPw ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8}
                  className="w-full h-11 px-4 pr-10 rounded-lg bg-[#1a1f2e] border border-white/10 text-white text-sm placeholder:text-[#64748b] ciq-input focus:outline-none"
                  placeholder="Minimum 8 characters" data-testid="signup-password-input" />
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748b] hover:text-white" data-testid="signup-toggle-pw">
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <div className="space-y-3 pt-1">
              <label className="flex items-start gap-2.5 cursor-pointer">
                <Checkbox checked={consentPromo} onCheckedChange={setConsentPromo} className="mt-0.5 border-white/20 data-[state=checked]:bg-[#4d7cfe] data-[state=checked]:border-[#4d7cfe]" data-testid="signup-consent-checkbox" />
                <span className="text-xs text-[#a0a8b8] leading-relaxed">I agree to receive promotional emails from CasinoIQ and casino partners</span>
              </label>
              <label className="flex items-start gap-2.5 cursor-pointer">
                <Checkbox checked={is18Plus} onCheckedChange={setIs18Plus} className="mt-0.5 border-white/20 data-[state=checked]:bg-[#4d7cfe] data-[state=checked]:border-[#4d7cfe]" data-testid="signup-age-checkbox" />
                <span className="text-xs text-[#a0a8b8] leading-relaxed">I am 18+ years old</span>
              </label>
            </div>

            <p className="text-[10px] text-[#64748b]">By signing up, you agree to our <span className="text-[#4d7cfe] cursor-pointer">Terms of Service</span> and <span className="text-[#4d7cfe] cursor-pointer">Privacy Policy</span>.</p>

            <button type="submit" disabled={loading}
              className="w-full h-12 rounded-xl bg-[#4d7cfe] text-white font-medium text-sm btn-lift disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="signup-submit-btn">
              {loading ? 'Creating account...' : <><UserPlus className="h-4 w-4" /> Create Account</>}
            </button>
          </form>

          <div className="relative my-5">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-white/10" /></div>
            <div className="relative flex justify-center"><span className="bg-[#20263a] px-3 text-xs text-[#64748b]">or</span></div>
          </div>

          <button onClick={handleGoogleSignup}
            className="w-full h-12 rounded-xl border border-white/10 bg-transparent text-white font-medium text-sm hover:bg-white/5 transition-colors flex items-center justify-center gap-2"
            data-testid="google-signup-btn">
            <svg className="h-4 w-4" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
            Continue with Google
          </button>
        </div>

        <p className="text-center mt-5 text-sm text-[#a0a8b8]">
          Already have an account? <Link to="/login" className="text-[#4d7cfe] hover:underline font-medium" data-testid="signup-login-link">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
